import { useEffect, useState, type ReactNode } from 'react';
import { fetchMemberships, HbsError } from '../api/hbs';
import type { AuthState } from '../api/request';
import { decideMembership, REMEMBERED_ORG_KEY, type Membership } from '../lib/memberships';
import { emailProblem } from '../lib/password-reset';
import { supabase } from '../lib/supabase';
import { IsianSandi } from '../components/IsianSandi';

/**
 * Layar masuk — menggantikan `DevSignIn`, yang meminta pengguna menempelkan JWT dan UUID
 * organisasi dengan tangan. Selama itu satu-satunya jalan masuk, jumlah orang yang dapat memakai
 * Workforce adalah nol: kurir tidak menempelkan JWT.
 *
 * Dua langkah, bukan satu:
 *
 *   1. Identitas — email dan kata sandi ke Supabase (sumber identitas yang SAMA dengan CRM).
 *   2. Organisasi — daftar keanggotaan dari HBS. Setiap endpoint Workforce menuntut
 *      `x-organization-id`, jadi ini bukan langkah tambahan yang bisa dilewati.
 *
 * Memisahkannya penting untuk pesan galat: login yang berhasil pada orang yang belum punya akses
 * organisasi BUKAN kegagalan login, dan tidak boleh berbunyi seperti kata sandi salah.
 */
export function SignInScreen({
  onSignIn,
}: {
  readonly onSignIn: (auth: AuthState) => void;
}): ReactNode {
  const [step, setStep] = useState<'identity' | 'organization' | 'recover'>('identity');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [options, setOptions] = useState<readonly Membership[]>([]);
  const [selected, setSelected] = useState<string>('');
  /*
   * Kegagalan JARINGAN saat mengambil daftar organisasi (langkah 2) — BEDA dari kata sandi
   * salah (langkah 1, yang sudah pasti berhasil pada titik ini). Ditemukan 23 Sep 2026: kurir
   * yang koneksi selulernya goyah sesaat mendapat "Failed to fetch" mentah, mengiranya kata
   * sandi salah, lalu MENGETIK ULANG dan login lagi dari nol — delapan sesi Supabase valid
   * tercipta hari itu, tak satu pun pernah sampai ke organisasi. Sesi langkah 1 SUDAH ada di
   * Supabase; yang perlu diulang cuma langkah 2, bukan seluruh proses masuk.
   */
  const [gagalJaringan, setGagalJaringan] = useState(false);

  // Sesi yang masih hidup dari kunjungan sebelumnya langsung dilanjutkan — inilah yang membuat
  // memuat ulang halaman tidak melemparkan orang kembali ke layar masuk.
  useEffect(() => {
    let aktif = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (aktif && data.session) {
        void lanjutkanKeOrganisasi(data.session.access_token);
      }
    });
    return () => {
      aktif = false;
    };
    // Sengaja sekali saat mount: ini pemulihan sesi, bukan reaksi terhadap perubahan state.
  }, []);

  async function lanjutkanKeOrganisasi(accessToken: string): Promise<void> {
    setError(null);
    setGagalJaringan(false);
    let daftar: readonly Membership[];
    try {
      daftar = await fetchMemberships(accessToken);
    } catch (gagal) {
      /*
       * `HbsError` = HBS sendiri yang menjawab (pesannya sudah manusiawi, lihat `bacaGalat`).
       * Selain itu = permintaannya tidak pernah sampai ke server — jaringan, bukan HBS yang
       * menolak. Kata sandi sudah pasti benar di titik ini; jangan biarkan pesannya terbaca
       * seperti itu yang salah.
       */
      setError(
        gagal instanceof HbsError
          ? gagal.message
          : 'Tidak bisa menghubungi server. Periksa koneksi internet Anda, lalu coba lagi.',
      );
      setGagalJaringan(!(gagal instanceof HbsError));
      setBusy(false);
      return;
    }

    const keputusan = decideMembership(daftar, localStorage.getItem(REMEMBERED_ORG_KEY));
    setBusy(false);

    if (keputusan.kind === 'none') {
      // Bukan galat login: orangnya terbukti ada, aksesnya yang belum.
      setNotice(
        'Masuk berhasil, tetapi akun ini belum terdaftar di organisasi mana pun. Hubungi admin organisasi Anda.',
      );
      setStep('organization');
      setOptions([]);
      return;
    }

    if (keputusan.kind === 'single') {
      masuk(accessToken, keputusan.membership.organizationId);
      return;
    }

    setOptions(keputusan.options);
    setSelected(keputusan.preselected?.organizationId ?? '');
    setStep('organization');
  }

  function masuk(accessToken: string, organizationId: string): void {
    localStorage.setItem(REMEMBERED_ORG_KEY, organizationId);
    onSignIn({ accessToken, organizationId });
  }

  /**
   * Mengulang HANYA langkah 2 — sesi Supabase langkah 1 sudah ada, tidak perlu kata sandi lagi.
   * Diambil ULANG dari Supabase (bukan disimpan di state sejak awal) karena bisa saja beberapa
   * saat berlalu antara kegagalan dan tombol ini ditekan; `getSession()` selalu memberi salinan
   * yang masih berlaku atau kabar bahwa sesinya sudah tidak ada lagi.
   */
  async function cobaLagiMuatOrganisasi(): Promise<void> {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      setGagalJaringan(false);
      setError('Sesi berakhir. Masuk kembali.');
      return;
    }
    setBusy(true);
    await lanjutkanKeOrganisasi(data.session.access_token);
  }

  async function kirimIdentitas(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    setGagalJaringan(false);

    const { data, error: gagal } = await supabase.auth.signInWithPassword({ email, password });
    if (gagal || !data.session) {
      setBusy(false);
      // Pesan disamakan untuk email salah dan kata sandi salah — membedakannya memberi tahu
      // penebak bahwa sebuah email terdaftar.
      setError('Email atau kata sandi salah.');
      return;
    }
    await lanjutkanKeOrganisasi(data.session.access_token);
  }

  /**
   * Mengirim tautan pemulihan.
   *
   * `redirectTo` menunjuk ke origin ini sendiri. Bila alamat itu belum terdaftar di daftar-izin
   * Supabase, Supabase memakai Site URL proyek sebagai gantinya — tautannya TETAP bekerja, hanya
   * mendarat di aplikasi saudara. Itu degradasi yang bisa diterima; yang tidak bisa diterima
   * adalah layar masuk tanpa jalan keluar sama sekali.
   */
  async function kirimPemulihan(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const masalah = emailProblem(email);
    if (masalah) {
      setError(masalah);
      return;
    }
    setBusy(true);
    setError(null);
    await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    /*
     * Berhasil dan gagal DISAMAKAN, dan galat dari Supabase sengaja tidak ditampilkan.
     * Membedakannya memberi tahu penebak apakah sebuah email terdaftar — kebocoran yang sama
     * dengan yang sudah dihindari pada pesan "Email atau kata sandi salah" di atas.
     */
    setStep('identity');
    setNotice(
      'Bila email itu terdaftar, tautan untuk mengatur ulang kata sandi sudah dikirim. Periksa ' +
        'kotak masuk dan folder spam.',
    );
  }

  async function pilihOrganisasi(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      setStep('identity');
      setError('Sesi berakhir. Masuk kembali.');
      return;
    }
    if (selected) masuk(data.session.access_token, selected);
  }

  async function keluar(): Promise<void> {
    await supabase.auth.signOut();
    setStep('identity');
    setOptions([]);
    setNotice(null);
    setError(null);
    setGagalJaringan(false);
  }

  if (step === 'recover') {
    return (
      <form className="card" onSubmit={(e) => void kirimPemulihan(e)}>
        <h2>Lupa kata sandi</h2>
        <p className="muted small">
          Masukkan email Anda. Kami kirimkan tautan untuk menyetel kata sandi baru.
        </p>
        {error ? <p role="alert">{error}</p> : null}
        <label htmlFor="email-pemulihan">Email</label>
        <input
          id="email-pemulihan"
          type="email"
          value={email}
          autoComplete="username"
          onChange={(e) => setEmail(e.target.value)}
        />
        <button type="submit" className="button-primary" disabled={busy}>
          {busy ? 'Mengirim…' : 'Kirim tautan'}
        </button>
        <button
          type="button"
          className="link-quiet"
          onClick={() => {
            setError(null);
            setStep('identity');
          }}
        >
          Kembali ke layar masuk
        </button>
      </form>
    );
  }

  if (step === 'organization') {
    return (
      <form className="card" onSubmit={(e) => void pilihOrganisasi(e)}>
        <h2>Pilih organisasi</h2>
        {notice ? <p className="muted">{notice}</p> : null}
        {error ? <p role="alert">{error}</p> : null}
        {options.map((o) => (
          <label key={o.organizationId} htmlFor={`org-${o.organizationId}`}>
            <input
              id={`org-${o.organizationId}`}
              type="radio"
              name="organization"
              value={o.organizationId}
              checked={selected === o.organizationId}
              onChange={() => setSelected(o.organizationId)}
            />
            {o.organizationName}
          </label>
        ))}
        {options.length > 0 ? (
          <button type="submit" className="button-primary" disabled={!selected}>
            Lanjut
          </button>
        ) : null}
        {/*
          Tindakan kedua, jadi ia TAUTAN — bukan tombol biru kedua di sebelah "Lanjut".
          Dua tombol sederajat pada layar pemilihan membuat orang menimbang mana yang benar,
          padahal salah satunya membatalkan seluruh proses masuk.
        */}
        <button type="button" className="link-quiet" onClick={() => void keluar()}>
          Keluar
        </button>
      </form>
    );
  }

  return (
    <form className="card" onSubmit={(e) => void kirimIdentitas(e)}>
      <h2>Masuk</h2>
      {notice ? <p className="muted small">{notice}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {/*
        Kata sandi SUDAH terbukti benar (langkah 1 lolos) — yang gagal cuma jaringan saat
        mengambil daftar organisasi. Coba Lagi mengulang HANYA itu, memakai sesi yang sudah ada,
        supaya orangnya tidak perlu mengetik ulang email+kata sandi untuk kegagalan yang bukan
        salahnya. Form di bawah tetap ada sebagai jalan keluar bila sesinya ternyata sudah habis.
      */}
      {gagalJaringan ? (
        <button
          type="button"
          className="button-primary"
          onClick={() => void cobaLagiMuatOrganisasi()}
          disabled={busy}
        >
          {busy ? 'Mencoba lagi…' : 'Coba lagi'}
        </button>
      ) : null}
      <label htmlFor="email">Email</label>
      <input
        id="email"
        type="email"
        value={email}
        autoComplete="username"
        required
        onChange={(e) => setEmail(e.target.value)}
      />
      <label htmlFor="password">Kata sandi</label>
      {/*
        Tombol mata — sama seperti layar masuk CRM. Alasannya (salah ketik di papan ketik
        ponsel) tercatat di components/IsianSandi.tsx.
      */}
      <IsianSandi
        id="password"
        value={password}
        autoComplete="current-password"
        required
        onChange={(e) => setPassword(e.target.value)}
      />
      <button type="submit" className="button-primary" disabled={busy}>
        {busy ? 'Memproses…' : 'Masuk'}
      </button>
      {/*
        Jalan keluar dari layar yang sedang menolak orangnya. Sebelum ini, satu-satunya cara
        mengatur ulang kata sandi ada di produk LAIN — yang tidak pernah dibuka pekerja lapangan
        dan tidak disebut di mana pun pada layar ini.
      */}
      <button
        type="button"
        className="link-quiet"
        onClick={() => {
          setError(null);
          setNotice(null);
          setStep('recover');
        }}
      >
        Lupa kata sandi?
      </button>
      {/*
        Trial 14 hari Workforce TERSENDIRI (BLD-015 lanjutan, 2 Okt 2026) — keputusan Owner
        eksplisit: tombol terpisah per produk, BUKAN redirect ke pendaftaran CRM (meski keduanya
        akhirnya memanggil endpoint HBS yang sama, `POST /onboarding`). Navigasi keras, bukan
        useNavigate() — layar ini dirender sebelum BrowserRouter dipasang di App.tsx, alasan sama
        TrialStartPage sendiri memakai window.location.assign.

        Menggantikan tombol "Coba demo gratis" (BLD-014b) yang mengarah ke `/demo` — Demo Instan
        sudah dihapus total (PR #1452, keputusan Owner yang sama: "hapus Demo Instan, ganti total
        ke Trial 14 hari").
      */}
      <button type="button" className="link-quiet" onClick={() => window.location.assign('/trial')}>
        Belum pelanggan? Mulai trial 14 hari gratis
      </button>
    </form>
  );
}
